<template>
  <div class="filterlist">
    <fieldset>
      <div class="row-flex--sm filtercontainer">
        <form id="filterForm" action="" @submit.prevent="$emit('submit')">

          <template v-if="config.EnableFreeTextSearch">
            <legend v-if="config.FreeTextHeading" class="h2">{{ config.FreeTextHeading }}</legend>
            <div role="search" class="input-group ui-front input_freetext">
              <label for="filter-freetext-input" class="sr-only">Sök</label>
              <input
                id="filter-freetext-input"
                :value="freeText"
                @input="$emit('update:freeText', ($event.target as HTMLInputElement).value)"
                type="text"
                name="fsq"
                class="form-control"
              />
              <span class="input-group-btn">
                <button type="submit" class="btn btn-primary">Sök</button>
              </span>
            </div>
          </template>

          <legend v-if="config.Heading" class="h2">{{ config.Heading }}</legend>

          <div class="row-flex--sm filterelements-hittatipssida">
            <div v-if="config.ShowAllButton">
              <input
                class="submit-button btn btn-default dropdown-toggle"
                :class="{ selected: !hasActiveFilters }"
                type="button"
                value="Alla"
                @click="$emit('reset')"
              />
            </div>
            <template v-for="element in config.FilterElements" :key="element.QueryStringParameter">
              <!-- Category filter -->
              <template v-if="element.Type === 'category'">
                <div class="dropdown filter-options__options" :class="{ open: openDropdownKey === element.QueryStringParameter }">
                  <button
                    class="btn btn-default dropdown-toggle"
                    :class="{ selected: activeCount(element) > 0 }"
                    :id="element.QueryStringParameter"
                    type="button"
                    aria-haspopup="true"
                    :aria-expanded="openDropdownKey === element.QueryStringParameter"
                    :aria-label="elementAriaLabel(element)"
                    @click.stop="toggleDropdown(element.QueryStringParameter)"
                  >
                    <span>{{ element.FilterHeading }} {{ activeCount(element) > 0 ? ` (${activeCount(element)}) ` : '' }}</span> 
                    <span class="caret"></span>
                  </button>
                  <div class="dropdown-menu" @click.stop>
                    <div class="container-header">
                      <button class="back-button" type="button" @click="closeDropdown($event)">
                        <span class="container-header-text">Tillbaka</span>
                      </button>
                    </div>
                    <fieldset class="container-outer">
                      <legend class="sr-only">{{ element.FilterTabName }}</legend>
                      <div class="filter-header">
                        <span class="filter-heading">{{ element.FilterTabName }}</span>
                        <a v-if="element.ShowModal"
                          href="#"
                          role="button"
                          data-modal-theme="modal-theme-bologna"
                          :aria-label="`Mer information om ${element.FilterHeading}`"
                          @click.prevent="openModal(element.QueryStringParameter)">
                          <svg-icon icon-name="information" aria-hidden="true" focusable="false" />
                          <span class="filter-header__info-text">Mer information</span>
                        </a>
                      </div>
                      <div class="container-inner">
                        <ul>
                          <li
                            v-for="choice in element.Choices"
                            :key="choice.Value"
                            :style="choice.Children && choice.Children.length > 0 ? 'break-inside: avoid' : ''"
                          >
                            <template v-if="choice.Children && choice.Children.length > 0">
                              <p class="choice-header">{{ choice.Text }}</p>
                              <ul class="child_list">
                                <input
                                  type="checkbox"
                                  class="parent"
                                  :id="`${element.QueryStringParameter}_${choice.Value}`"
                                  :name="element.QueryStringParameter"
                                  :value="choice.Value"
                                  v-model="selectedValues[element.QueryStringParameter]"
                                />
                                <label :for="`${element.QueryStringParameter}_${choice.Value}`" class="container-choice">
                                  <svg-icon icon-name="check" />
                                  <span>Allt {{ lcFirst(choice.Text) }}</span>
                                </label>
                                <li v-for="child in choice.Children" :key="child.Value">
                                  <input
                                    type="checkbox"
                                    class="child"
                                    :id="`${element.QueryStringParameter}_${child.Value}`"
                                    :name="element.QueryStringParameter"
                                    :value="child.Value"
                                    v-model="selectedValues[element.QueryStringParameter]"
                                  />
                                  <label :for="`${element.QueryStringParameter}_${child.Value}`" class="container-choice">
                                    <svg-icon icon-name="check" />
                                    <svg-icon icon-name="minus" />
                                    <span>{{ child.Text }}</span>
                                  </label>
                                </li>
                              </ul>
                            </template>
                            <template v-else>
                              <input
                                type="checkbox"
                                class="parent"
                                :id="`${element.QueryStringParameter}_${choice.Value}`"
                                :name="element.QueryStringParameter"
                                :value="choice.Value"
                                v-model="selectedValues[element.QueryStringParameter]"
                              />
                              <label :for="`${element.QueryStringParameter}_${choice.Value}`" class="container-choice">
                                <svg-icon icon-name="check" />
                                <svg-icon icon-name="minus" />
                                <span>{{ choice.Text }}</span>
                              </label>
                            </template>
                          </li>
                        </ul>
                      </div>
                    </fieldset>
                    <div class="container-submit">
                      <input
                        class="submit-button btn btn-primary"
                        type="button"
                        value="Filtrera"
                        @click="submitFromDropdown($event)"
                      />
                    </div>
                  </div>
                  <div v-if="element.ShowModal"
                       :id="`filterInformationModal_${element.QueryStringParameter}`"
                       class="modal" tabindex="-1" role="dialog">
                    <div class="modal-dialog" role="document">
                      <div class="modal-content">
                        <div class="modal-header">
                          <button type="button" class="close" data-dismiss="modal" aria-label="Stäng dialog">
                            <svg-icon icon-name="kryss" />
                          </button>
                        </div>
                        <div class="modal-body">
                          <h2 class="modal-heading">{{ element.ModalHeading }}</h2>
                          <div v-html="element.ModalText"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
              <template v-if="element.Type === 'list'">
                <div class="dropdown filter-options__options" :class="{ open: openDropdownKey === element.QueryStringParameter }">
                  <button
                    class="btn btn-default dropdown-toggle"
                    :class="{ selected: activeCount(element) > 0 }"
                    :id="element.QueryStringParameter"
                    type="button"
                    aria-haspopup="true"
                    :aria-expanded="openDropdownKey === element.QueryStringParameter"
                    :aria-label="elementAriaLabel(element)"
                    @click.stop="toggleDropdown(element.QueryStringParameter)"
                  >
                    <span>{{ element.FilterHeading }} {{ activeCount(element) > 0 ? ` (${activeCount(element)}) ` : '' }}</span>
                    <span class="caret"></span>
                  </button>
                  <div class="dropdown-menu" @click.stop>
                    <div class="container-header">
                      <button class="back-button" type="button" @click="closeDropdown($event)">
                        <span class="container-header-text">Tillbaka</span>
                      </button>
                    </div>
                    <fieldset class="container-outer">
                      <legend class="sr-only">{{ element.FilterTabName }}</legend>
                      <div class="filter-header">
                        <span class="filter-heading">{{ element.FilterTabName }}</span>
                      </div>
                      <div class="container-inner">
                        <ul>
                          <li v-for="choice in element.Choices" :key="choice.Value">
                            <input
                              type="checkbox"
                              class="parent"
                              :id="`${element.QueryStringParameter}_${choice.Value}`"
                              :name="element.QueryStringParameter"
                              :value="choice.Value"
                              v-model="selectedValues[element.QueryStringParameter]"
                            />
                            <label :for="`${element.QueryStringParameter}_${choice.Value}`" class="container-choice">
                              <svg-icon icon-name="check" />
                              <svg-icon icon-name="minus" />
                              <span>{{ choice.Text }}</span>
                            </label>
                          </li>
                        </ul>
                      </div>
                    </fieldset>
                    <div class="container-submit">
                      <input
                        class="submit-button btn btn-primary"
                        type="button"
                        value="Filtrera"
                        @click="submitFromDropdown($event)"
                      />
                    </div>
                  </div>
                </div>
              </template>

            </template>
          </div>

          <div class="container-submit">
            <input class="submit-button btn btn-primary" type="submit" value="Filtrera" />
          </div>

        </form>
      </div>
    </fieldset>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ref, onMounted, onUnmounted, PropType } from 'vue';
import SvgIcon from '../components/svgicon.vue';
import { FilterConfigDto, FilterElementDto } from './hittaTipsSidaService';

export default defineComponent({
  name: 'TipsFilter',
  components: { SvgIcon },
  props: {
    config: { type: Object as PropType<FilterConfigDto>, required: true },
    freeText: { type: String, required: true },
    selectedValues: { type: Object as PropType<Record<string, string[]>>, required: true },
  },
  emits: ['update:freeText', 'submit', 'reset'],
  setup(props, { emit }) {
    function activeCount(element: FilterElementDto): number {
      return props.selectedValues[element.QueryStringParameter]?.length ?? 0;
    }

    const totalActiveCount = computed(() =>
      Object.values(props.selectedValues).reduce((sum, arr) => sum + arr.length, 0)
    );

    const hasActiveFilters = computed(() => totalActiveCount.value > 0 || props.freeText.length > 0);

    function elementAriaLabel(element: FilterElementDto): string {
      const count = activeCount(element);
      if (count === 0) return '';
      return `${element.FilterHeading}, ${count} alternativ ${count > 1 ? 'valda,' : 'valt,'}`;
    }

    function lcFirst(str: string): string {
      return str.charAt(0).toLowerCase() + str.slice(1);
    }

    const openDropdownKey = ref<string | null>(null);

    function toggleDropdown(key: string) {
      openDropdownKey.value = openDropdownKey.value === key ? null : key;
    }

    function closeAllDropdowns() {
      if(openDropdownKey.value) {
        openDropdownKey.value = null;
        emit('submit');
      }
    }

    onMounted(() => document.addEventListener('click', closeAllDropdowns));
    onUnmounted(() => document.removeEventListener('click', closeAllDropdowns));

    function closeDropdown(_event: Event) {
      openDropdownKey.value = null;
    }

    function submitFromDropdown(event: Event) {
      closeDropdown(event);
      emit('submit');
    }

    function openModal(queryStringParameter: string) {
      ($(`#filterInformationModal_${queryStringParameter}`)).modal('show');
    }

    return {
      openDropdownKey,
      toggleDropdown,
      activeCount,
      totalActiveCount,
      hasActiveFilters,
      elementAriaLabel,
      lcFirst,
      closeDropdown,
      submitFromDropdown,
      openModal,
    };
  }
});
</script>
